/**
 * StarDisplay - Sterne-Anzeige mit optionalem Score
 *
 * Zeigt einen Score als Sterne-Bewertung an.
 * Kann als Badge oder inline verwendet werden.
 */

import { Star } from 'lucide-react';
import { getStarClasses } from '@/config/ui.config';
import { cn } from '@/lib/utils';

interface StarDisplayProps {
  /** Score-Wert (0-5, kann auch Dezimalzahlen sein) */
  score: number | null;
  /** Numerischen Wert anzeigen */
  showValue?: boolean;
  /** Größe */
  size?: 'sm' | 'md' | 'lg';
  /** Als Badge mit Hintergrund darstellen */
  asBadge?: boolean;
  /** Zusätzliche CSS-Klassen */
  className?: string;
}

const STAR_SIZES = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

const VALUE_SIZES = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg font-semibold',
};

const BADGE_SIZES = {
  sm: 'px-2 py-0.5',
  md: 'px-2.5 py-1',
  lg: 'px-3 py-1.5',
};

export function StarDisplay({
  score,
  showValue = true,
  size = 'md',
  asBadge = false,
  className,
}: StarDisplayProps): JSX.Element {
  if (score === null || score === undefined) {
    return (
      <div
        className={cn(
          'flex items-center gap-1',
          asBadge && ['rounded-full', BADGE_SIZES[size], 'bg-gray-100'],
          className
        )}
      >
        <Star className={cn(STAR_SIZES[size], 'text-gray-300')} />
        {showValue && <span className={cn(VALUE_SIZES[size], 'text-gray-400')}>–</span>}
      </div>
    );
  }

  const styles = getStarClasses(score);
  const roundedScore = Math.round(score * 10) / 10;

  // Anzahl gefüllter Sterne (gerundet)
  const filledStars = Math.round(score);

  return (
    <div
      className={cn(
        'flex items-center gap-1.5',
        asBadge && ['rounded-full', BADGE_SIZES[size], styles.bg],
        className
      )}
    >
      {/* Mini-Sterne-Anzeige */}
      <div className="flex items-center gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className={cn(
              STAR_SIZES[size],
              i < filledStars ? [styles.starColor, 'fill-current'] : 'text-gray-300'
            )}
          />
        ))}
      </div>

      {/* Numerischer Wert */}
      {showValue && (
        <span className={cn(VALUE_SIZES[size], styles.text, 'font-medium')}>
          {roundedScore.toFixed(1)}
        </span>
      )}
    </div>
  );
}

/**
 * Große Badge-Variante für prominente Anzeige (z.B. Header)
 */
export function StarScoreBadge({
  score,
  label,
  className,
}: {
  score: number | null;
  label?: string;
  className?: string;
}): JSX.Element {
  const styles = score !== null ? getStarClasses(score) : getStarClasses(0);
  const roundedScore = score !== null ? Math.round(score * 10) / 10 : null;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center',
        'rounded-xl p-4',
        score !== null ? styles.bg : 'bg-gray-100',
        className
      )}
    >
      {/* Sterne */}
      <div className="flex items-center gap-1 mb-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className={cn(
              'w-6 h-6',
              score !== null && i < Math.round(score)
                ? [styles.starColor, 'fill-current']
                : 'text-gray-300'
            )}
          />
        ))}
      </div>

      {/* Score */}
      <div
        className={cn(
          'text-3xl font-bold',
          score !== null ? styles.text : 'text-gray-400'
        )}
      >
        {roundedScore !== null ? roundedScore.toFixed(1) : '–'}
      </div>

      {/* Label */}
      {label && (
        <div className="text-sm text-gray-600 mt-1">{label}</div>
      )}
    </div>
  );
}

/**
 * Kompakte Inline-Variante für Listen
 */
export function StarScoreInline({
  score,
  className,
}: {
  score: number | null;
  className?: string;
}): JSX.Element {
  return (
    <StarDisplay
      score={score}
      size="sm"
      showValue
      asBadge={false}
      className={className}
    />
  );
}

/**
 * Nur der numerische Wert mit Farbcodierung
 */
export function StarScoreValue({
  score,
  size = 'md',
  className,
}: {
  score: number | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}): JSX.Element {
  if (score === null) {
    return (
      <span className={cn(VALUE_SIZES[size], 'text-gray-400', className)}>–</span>
    );
  }

  const styles = getStarClasses(score);
  const roundedScore = Math.round(score * 10) / 10;

  return (
    <span className={cn(VALUE_SIZES[size], styles.text, 'font-semibold', className)}>
      {roundedScore.toFixed(1)}
    </span>
  );
}
